/**
 * GA4 dataLayer bridge — reuses a site's existing analytics instrumentation.
 *
 * When enabled (website setting `dataLayerBridge`), the script observes
 * `window.dataLayer` and maps GA4 e-commerce pushes onto our canonical
 * events, so a store that already has GA4/GTM e-commerce tracking needs no
 * extra tagging. Two push shapes are supported:
 *
 * 1. GTM convention (object push):  dataLayer.push({event: "purchase", ecommerce: {...}})
 * 2. gtag convention (Arguments):   gtag("event", "purchase", {...}) — which
 *    pushes an array-like Arguments object onto the dataLayer.
 *
 * Besides the fixed GA4 names, the customer's own key events come along too,
 * provided they named them (`dataLayerEvents`). Which name gets through and
 * why lives in data-layer-event-names.ts.
 *
 * Loop/duplicate safety:
 * - `state.selfDispatch` is true while OUR code calls gtag() (GA4/Google
 *   pixel channel); such entries are skipped.
 * - Conversions go through claimConversion() (first-wins vs the e-commerce
 *   auto-detector) and purchases get a deterministic "ord_<id>" eventId.
 * - Events already covered by a manual tracking rule on this URL are skipped.
 */

import { state, debugLog } from "./state.js";
import { shopCurrency } from "./shop-currency.js";
import { sendEvent } from "./event-transport.js";
import { findMatchingRules } from "./url-matching.js";
import { claimConversion, orderEventId } from "./conversion-claims.js";
import {
  GA4_EVENT_MAP,
  canonicalNameFor,
  claimKeyFor,
  isOrderBound,
  pickFromReplay,
} from "./data-layer-event-names.js";

const MAX_ITEM_IDS = 25;

interface Ga4EcommercePayload {
  value?: unknown;
  currency?: unknown;
  transaction_id?: unknown;
  items?: unknown;
}

function hasManualRule(eventName: string): boolean {
  const rules = findMatchingRules(window.location.href);
  return rules.some((r) => r.eventName === eventName);
}

/**
 * The products in a dataLayer push.
 *
 * Two shapes are in the wild and both have to work. GA4 names the field
 * `item_id`; the Google Ads remarketing format, which is what Pixel Manager for
 * WooCommerce actually pushes, names it `id`. Reading only `item_id` found
 * nothing on a real Pixel Manager store, so the event went to Meta with a value
 * and no products at all. `content_ids` is what Meta matches against the
 * catalogue, so without it a ViewContent or AddToCart can never drive a
 * catalogue ad, and it is accepted with a 200 either way.
 *
 * `quantity` comes along because Meta's `contents` wants id and quantity, and
 * because counting order lines instead of items understates every basket with
 * more than one of something.
 */
function extractItems(
  items: unknown,
): { contentIds: string[]; contents: Array<{ id: string; quantity: number }>; numItems: number } | null {
  if (!Array.isArray(items) || items.length === 0) return null;
  const contentIds: string[] = [];
  const contents: Array<{ id: string; quantity: number }> = [];
  let numItems = 0;

  for (let i = 0; i < items.length && contentIds.length < MAX_ITEM_IDS; i++) {
    const item = items[i] as { item_id?: unknown; id?: unknown; item_name?: unknown; quantity?: unknown } | null;
    if (!item) continue;

    const rawId = item.item_id != null ? item.item_id : item.id;
    const id = rawId != null ? String(rawId) : item.item_name != null ? String(item.item_name) : null;
    if (!id) continue;

    const quantity = Number(item.quantity);
    const count = isFinite(quantity) && quantity > 0 ? quantity : 1;
    contentIds.push(id);
    contents.push({ id, quantity: count });
    numItems += count;
  }

  if (contentIds.length === 0) return null;
  return { contentIds, contents, numItems };
}

function bridgeEvent(dataLayerName: string, params: Ga4EcommercePayload): void {
  const eventName = canonicalNameFor(dataLayerName, state.config?.dataLayerEvents);
  if (!eventName) return;

  if (hasManualRule(eventName)) {
    debugLog("dataLayerBridge", "skipped " + eventName + " — manual rule covers this URL");
    return;
  }

  const transactionId = params.transaction_id != null ? String(params.transaction_id) : undefined;

  // The claim exists to stop two SOURCES from reporting the same conversion:
  // the store auto-detector and this bridge both look at the same Shopify
  // purchase. That only applies to the standard names, because the detector
  // knows nothing about the customer's own names.
  //
  // For a custom key event the same claim would throw away a second, genuine
  // push on the same page. Someone requesting two quotes would see one. That
  // is the failure mode we refuse: silent loss. So a duplicate push from the
  // customer counts twice, and that is visible; a missing request would not be.
  const isStandardName = GA4_EVENT_MAP[dataLayerName] != null;

  // Een andere bron bezit de trechter van deze winkel.
  //
  // `data-tst-ecommerce="off"` zet de DOM-heuristiek stil, maar dit is de
  // tweede waarnemer van hetzelfde: een winkel met Pixel Manager ernaast duwt
  // add_to_cart ook de dataLayer in. Op de testwinkel leverde één klik op "in
  // winkelwagen" daardoor twee AddToCart-events op, één van Pixel Manager via
  // deze brug en één van WooCommerce via onze plugin. De claim ving dat niet:
  // de klassieke add-to-cart herlaadt de pagina en een claim leeft één lading.
  //
  // Alleen de standaardnamen staan stil. De eigen events van de winkelier,
  // waar deze brug voor bedoeld is, blijven gewoon doorkomen.
  if (isStandardName && state.ecommerceDisabled) {
    debugLog("dataLayerBridge", "skipped " + eventName + " — the shop's own plugin reports the funnel");
    return;
  }

  // An order-bound push without an order id waits for a better one.
  //
  // Pixel Manager announces a purchase TWICE: a remarketing push with no order
  // id, then the conversion push with one. The claim keys an order-bound event
  // on its name, so exactly one of those two gets through, and the one that
  // arrives first is the one WITHOUT the id. It then gets a random event id
  // instead of ord_<order>, and no longer collapses against the same purchase
  // coming from the server. Order 31 on the test shop went to Meta twice, under
  // two ids, on 6 August 2026. Both were accepted.
  //
  // The replay on install already picks the better of the two, because there it
  // can look at all of them at once. Live pushes cannot be looked ahead at, so
  // this waits instead. If the better push never comes, this one is sent after
  // all: a purchase that is hard to recognise later still beats no purchase.
  if (isStandardName && isOrderBound(eventName) && !transactionId) {
    debugLog("dataLayerBridge", "holding " + eventName + " — no order id yet");
    const send = () => {
      pendingOrderBound = pendingOrderBound.filter((entry) => entry !== send);
      if (!claimConversion(eventName, claimKeyFor(eventName, undefined, window.location.href))) {
        debugLog("dataLayerBridge", "dropped " + eventName + " — a push with an order id won");
        return;
      }
      dispatchBridgedEvent(eventName, params, undefined);
    };
    pendingOrderBound.push(send);
    setTimeout(() => {
      if (pendingOrderBound.indexOf(send) === -1) return;
      send();
    }, ORDER_ID_WAIT_MS);
    return;
  }

  if (isStandardName) {
    const claimKey = claimKeyFor(eventName, transactionId, window.location.href);
    if (!claimConversion(eventName, claimKey)) {
      debugLog("dataLayerBridge", "skipped " + eventName + " — already claimed (e-commerce detector or earlier push)");
      return;
    }
  }

  dispatchBridgedEvent(eventName, params, transactionId);
}

/**
 * How long an order-bound push waits for a version carrying an order id.
 *
 * Pixel Manager emits both within the same tick; this is generous so a slower
 * plugin still wins the race.
 */
const ORDER_ID_WAIT_MS = 1000;

/** Held sends, flushed if the page goes away first. */
let pendingOrderBound: Array<() => void> = [];

function flushPendingOrderBound(): void {
  const pending = pendingOrderBound;
  pendingOrderBound = [];
  pending.forEach((send) => send());
}

function dispatchBridgedEvent(
  eventName: string,
  params: Ga4EcommercePayload,
  transactionId: string | undefined,
): void {

  const value = typeof params.value === "number" ? params.value : parseFloat(String(params.value));
  const items = extractItems(params.items);
  const customData: Record<string, unknown> = {};
  if (transactionId) customData.order_id = transactionId;
  if (items) {
    customData.content_ids = items.contentIds;
    // Meta reads `contents` for the catalogue and `content_ids` for the older
    // matching path, and wants id plus quantity in the first. Sending both is
    // what their own documentation asks for.
    customData.contents = items.contents;
    customData.num_items = items.numItems;
    customData.content_type = "product";
  }

  debugLog("dataLayerBridge", "bridging " + eventName, "value=" + value, "txid=" + transactionId);

  sendEvent({
    eventName,
    // An order id makes the conversion recognisable across page loads, and
    // that holds for a custom name just as much as for Purchase: refreshing
    // the thank-you page must not count twice.
    eventId: transactionId ? orderEventId(transactionId) : undefined,
    eventValue: isNaN(value) ? undefined : value,
    // The shop's own currency when the push does not name one. Pixel Manager's
    // add_to_cart carries a value and no currency, and without this the amount
    // travelled on to be labelled with whatever default sat in our settings
    // screen. A dollar shop reported euros.
    currency: params.currency != null ? String(params.currency) : shopCurrency(),
    customData: Object.keys(customData).length > 0 ? customData : undefined,
    source: "datalayer",
  });
}

/**
 * Pull out what a dataLayer entry holds, without acting on it.
 *
 * Separate from sending because the replay on install has to look at ALL
 * entries before it picks one. See pickFromReplay.
 */
function parseEntry(entry: unknown): { name: string; params: Ga4EcommercePayload } | null {
  if (!entry || typeof entry !== "object") return null;

  // gtag convention: array-like ("event", name, params)
  const argLike = entry as { [index: number]: unknown; length?: unknown };
  if (typeof argLike.length === "number" && argLike[0] === "event" && typeof argLike[1] === "string") {
    const params = (argLike[2] && typeof argLike[2] === "object" ? argLike[2] : {}) as Ga4EcommercePayload;
    return { name: argLike[1] as string, params };
  }

  // GTM convention: {event: "purchase", ecommerce: {...}}
  const objectPush = entry as { event?: unknown; ecommerce?: unknown };
  if (typeof objectPush.event !== "string") return null;
  // canonicalNameFor decides which name gets through, one layer deeper in
  // bridgeEvent. That same judgement used to sit here as well (a hard test
  // against the GA4 map), and that was exactly the line that let every custom
  // key event fall silent. One decider is enough.
  // {ecommerce: null} reset pushes carry no data
  const ecommerce = objectPush.ecommerce && typeof objectPush.ecommerce === "object"
    ? (objectPush.ecommerce as Ga4EcommercePayload)
    : (objectPush as Ga4EcommercePayload);
  return { name: objectPush.event, params: ecommerce };
}

/** Handle one dataLayer entry (object push or gtag Arguments). */
function processEntry(entry: unknown): void {
  if (state.selfDispatch) return; // our own gtag() call
  const parsed = parseEntry(entry);
  if (!parsed) return;
  bridgeEvent(parsed.name, parsed.params);
}

export function installDataLayerBridge(): void {
  const w = window as Window & { dataLayer?: unknown[] };
  if (!Array.isArray(w.dataLayer)) {
    w.dataLayer = [];
  }
  const dataLayer = w.dataLayer;

  // Replay what was already there, but not blindly.
  //
  // Pixel Manager for WooCommerce announces one purchase TWICE: first a
  // remarketing push without an order id, then the conversion with one. Taking
  // the first sends a purchase that cannot be recognised after a reload,
  // because the order id is precisely what makes it unique.
  const parsed = dataLayer.map(parseEntry);
  const names = parsed.map((entry) => ({
    name: entry ? canonicalNameFor(entry.name, state.config?.dataLayerEvents) : null,
    hasId: !!(entry && entry.params.transaction_id != null),
  }));
  const allowed = pickFromReplay(names);
  parsed.forEach((entry, i) => {
    if (!entry || !allowed[i]) return;
    processEntry(dataLayer[i]);
  });

  // Intercept future pushes; chain the original so GTM/gtag keep working
  const originalPush = dataLayer.push.bind(dataLayer);
  dataLayer.push = function (...entries: unknown[]): number {
    const result = originalPush(...entries);
    entries.forEach(processEntry);
    return result;
  };

  // A purchase held for a better push must not vanish with the page.
  window.addEventListener("pagehide", flushPendingOrderBound);

  debugLog("dataLayerBridge", "installed", "replayed=" + dataLayer.length);
}
